import { Graph } from '@antv/x6';

/**
 * 原生 SVG 节点定义
 *
 * 完全避开 foreignObject 和 React 组件，使用纯 SVG 元素渲染节点。
 * 解决 Safari 中 foreignObject 内容不跟随父 <g> transform 的问题。
 */

const commonBody = {
  rx: 16,
  ry: 16,
  refWidth: '100%',
  refHeight: '100%',
};

const handlePorts = {
  groups: {
    top: {
      position: 'top',
      attrs: {
        circle: {
          r: 5,
          fill: '#fff',
          stroke: '#3BC3FF',
          strokeWidth: 2,
          opacity: 0,
          pointerEvents: 'visibleStroke',
          cursor: 'crosshair',
        },
      },
    },
    bottom: {
      position: 'bottom',
      attrs: {
        circle: {
          r: 5,
          fill: '#fff',
          stroke: '#3BC3FF',
          strokeWidth: 2,
          opacity: 0,
          pointerEvents: 'visibleStroke',
          cursor: 'crosshair',
        },
      },
    },
    left: {
      position: 'left',
      attrs: {
        circle: {
          r: 5,
          fill: '#fff',
          stroke: '#3BC3FF',
          strokeWidth: 2,
          opacity: 0,
          pointerEvents: 'visibleStroke',
          cursor: 'crosshair',
        },
      },
    },
    right: {
      position: 'right',
      attrs: {
        circle: {
          r: 5,
          fill: '#fff',
          stroke: '#3BC3FF',
          strokeWidth: 2,
          opacity: 0,
          pointerEvents: 'visibleStroke',
          cursor: 'crosshair',
        },
      },
    },
  },
  items: [
    { id: 'port-top', group: 'top' },
    { id: 'port-bottom', group: 'bottom' },
    { id: 'port-left', group: 'left' },
    { id: 'port-right', group: 'right' },
  ],
};

const optionPorts = {
  ...handlePorts,
  groups: {
    ...handlePorts.groups,
    arrow: {
      position: {
        name: 'top',
        args: { dx: 0, dy: -6 },
      },
      markup: [
        { tagName: 'circle', selector: 'arrowAnchor' },
        { tagName: 'line', selector: 'arrowLine' },
        { tagName: 'path', selector: 'arrowHead' },
      ],
      attrs: {
        arrowAnchor: {
          r: 4,
          cx: 0,
          cy: 0,
          fill: 'transparent',
          stroke: 'transparent',
          magnet: true,
          pointerEvents: 'none',
          class: 'opm-node-port',
        },
        arrowLine: {
          x1: 0,
          y1: 0,
          x2: 0,
          y2: -14,
          stroke: '#595959',
          strokeWidth: 2,
          strokeLinecap: 'round',
          pointerEvents: 'visibleStroke',
          cursor: 'crosshair',
          class: 'opm-node-port',
        },
        arrowHead: {
          d: 'M -6 -14 L 0 -22 L 6 -14 Z',
          fill: '#595959',
          stroke: '#595959',
          strokeWidth: 1,
          pointerEvents: 'visiblePainted',
          cursor: 'crosshair',
          class: 'opm-node-port',
        },
      },
    },
  },
  items: [...handlePorts.items, { id: 'port-arrow', group: 'arrow' }],
};

const commonText = {
  textAnchor: 'middle',
  textVerticalAnchor: 'middle',
  fill: '#263238',
  fontSize: 16,
  fontWeight: 600,
};

const headerText = {
  textAnchor: 'middle',
  textVerticalAnchor: 'middle',
  fill: '#1565C0',
  fontSize: 12,
  fontWeight: 500,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
};

const processHeaderText = {
  textAnchor: 'middle',
  textVerticalAnchor: 'middle',
  fill: '#D84315',
  fontSize: 12,
  fontWeight: 500,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
};

interface GradientSpec {
  id: string;
  type: string;
  stops: { offset: number; color: string; opacity?: number }[];
  attrs?: Record<string, any>;
}

export const GradientSpecs: Record<string, GradientSpec> = {
  objectBody: {
    id: 'opm-gradient-object-body',
    type: 'linearGradient',
    stops: [
      { offset: 0, color: '#E6EBF5' },
      { offset: 0.5, color: '#F1F5FB' },
      { offset: 0.85, color: '#F8F9FA' },
      { offset: 1, color: '#FFFFFF' },
    ],
    attrs: {
      x1: '0%',
      y1: '100%',
      x2: '0%',
      y2: '0%',
      gradientUnits: 'objectBoundingBox',
    },
  },
  objectHeader: {
    id: 'opm-gradient-object-header',
    type: 'linearGradient',
    stops: [
      { offset: 0, color: '#8DECA0' },
      { offset: 1, color: '#5BC878' },
    ],
    attrs: {
      x1: '0%',
      y1: '0%',
      x2: '0%',
      y2: '100%',
      gradientUnits: 'objectBoundingBox',
    },
  },
  processBody: {
    id: 'opm-gradient-process-body',
    type: 'linearGradient',
    stops: [
      { offset: 0, color: '#E6EBF5' },
      { offset: 0.5, color: '#F1F5FB' },
      { offset: 0.85, color: '#F8F9FA' },
      { offset: 1, color: '#FFFFFF' },
    ],
    attrs: {
      x1: '0%',
      y1: '100%',
      x2: '0%',
      y2: '0%',
      gradientUnits: 'objectBoundingBox',
    },
  },
  stateBody: {
    id: 'opm-gradient-state-body',
    type: 'linearGradient',
    stops: [
      { offset: 0, color: '#FFF7E6' },
      { offset: 1, color: '#FFE0B0' },
    ],
    attrs: {
      x1: '0%',
      y1: '0%',
      x2: '0%',
      y2: '100%',
      gradientUnits: 'objectBoundingBox',
    },
  },
  valueBody: {
    id: 'opm-gradient-value-body',
    type: 'linearGradient',
    stops: [
      { offset: 0, color: '#EEF2FF' },
      { offset: 1, color: '#C8D2F5' },
    ],
    attrs: {
      x1: '0%',
      y1: '0%',
      x2: '0%',
      y2: '100%',
      gradientUnits: 'objectBoundingBox',
    },
  },
};

export const objectNodeMarkup = [
  {
    tagName: 'rect',
    selector: 'body',
    attrs: {
      fill: `url(#${GradientSpecs.objectBody.id})`,
      stroke: '#70E483',
      strokeWidth: 2,
      filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.08))',
      ...commonBody,
    },
  },
  {
    tagName: 'rect',
    selector: 'header',
    attrs: {
      fill: `url(#${GradientSpecs.objectHeader.id})`,
      stroke: 'none',
      x: 0,
      y: 0,
      rx: 6,
      ry: 6,
      refWidth: '100%',
      height: 22,
    },
  },
  {
    tagName: 'rect',
    selector: 'headerTextBg',
    attrs: {
      refWidth: '60%',
      refHeight: '60%',
      refX: '50%',
      refY: '22%',
      rx: 12,
      ry: 12,
      fill: '#FF7043',
      pointerEvents: 'none',
    },
  },
  // {
  //   tagName: 'text',
  //   selector: 'headerText',
  //   className: 'opm-node-title',
  //   textContent: 'Object',
  //   attrs: {
  //     ...headerText,
  //     refX: '50%',
  //     refY: '20%',
  //   },
  // },
  {
    tagName: 'text',
    selector: 'label',
    className: 'opm-node-label',
    attrs: {
      ...commonText,
      refX: '50%',
      refY: '55%',
      textVerticalAnchor: 'middle',
    },
  },
];

export const optionNodeMarkup = [...objectNodeMarkup];

/** 过程节点：椭圆、青绿色边框与淡绿色背景 */
export const processNodeMarkup = [
  {
    tagName: 'ellipse',
    selector: 'body',
    attrs: {
      fill: `url(#${GradientSpecs.processBody.id})`,
      stroke: '#3BC3FF',
      strokeWidth: 2,
      filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.08))',
    },
  },
  {
    tagName: 'rect',
    selector: 'header',
    attrs: {
      refWidth: '70%',
      height: 24,
      refX: '50%',
      refY: '-20%',
      x: '-35%',
      y: '-18%',
      rx: 6,
      ry: 6,
      fill: '#FBE9E7',
      stroke: 'none',
      pointerEvents: 'none',
    },
  },
  {
    tagName: 'text',
    selector: 'headerText',
    className: 'opm-node-title',
    textContent: 'Process',
    attrs: {
      ...processHeaderText,
      refX: '50%',
      refY: '-12%',
    },
  },
  {
    tagName: 'text',
    selector: 'label',
    className: 'opm-node-label',
    attrs: {
      ...commonText,
      refX: '50%',
      refY: '55%',
    },
  },
];

/** 状态节点：橙色圆角矩形 */
export const stateNodeMarkup = [
  {
    tagName: 'rect',
    selector: 'body',
    className: 'opm-node-move-area',
    attrs: {
      fill: `url(#${GradientSpecs.stateBody.id})`,
      stroke: '#fa8c16',
      strokeWidth: 2,
      ...commonBody,
    },
  },
  {
    tagName: 'text',
    selector: 'label',
    className: 'opm-node-label',
    attrs: {
      ...commonText,
      refX: '50%',
      refY: '50%',
    },
  },
];

/** 值节点：蓝色圆角矩形 */
export const valueNodeMarkup = [
  {
    tagName: 'rect',
    selector: 'body',
    className: 'opm-node-move-area',
    attrs: {
      fill: `url(#${GradientSpecs.valueBody.id})`,
      stroke: '#4169E1',
      strokeWidth: 2,
      ...commonBody,
    },
  },
  {
    tagName: 'text',
    selector: 'label',
    className: 'opm-node-label',
    attrs: {
      ...commonText,
      refX: '50%',
      refY: '50%',
    },
  },
];


function registerShape(
  shape: string,
  markup: any[],
  attrs: Record<string, any> = {},
  ports = handlePorts,
) {
  Graph.registerNode(shape, {
    markup,
    attrs: {
      body: {
        refWidth: '100%',
        refHeight: '100%',
      },
      label: {
        text: '',
      },
      ...attrs,
    },
    ports: ports,
    propHooks(metadata: any) {
      const data = metadata.data || {};
      const label = data.label || shape.replace('opm-', '');
      const selected = data.selected;
      const active = data.active;

      if (metadata.attrs == null) {
        metadata.attrs = {};
      }
      if (metadata.attrs.label == null) {
        metadata.attrs.label = {};
      }
      metadata.attrs.label.text = label;
      if (data.childStateCount) {
        if (shape === 'opm-process') {
          metadata.attrs.headerText = {
            ...processHeaderText,
            refX: '50%',
            refY: 20,
          };
          metadata.attrs.label.refY = 40;
        } else {
          metadata.attrs.label.refY = '25%';
        }
      }

      if (shape === 'opm-object' || shape === 'opm-option') {
        if (metadata.attrs.label == null) {
          metadata.attrs.label = {};
        }
        if (data.childStateCount && data.childStateCount > 0) {
          metadata.attrs.label.refY = 26;
          metadata.attrs.label.textVerticalAnchor = 'top';
        } else {
          metadata.attrs.label.refY = '55%';
          metadata.attrs.label.textVerticalAnchor = 'middle';
        }
      }

      if (metadata.attrs.body == null) {
        metadata.attrs.body = {};
      }
      if (selected) {
        metadata.attrs.body.stroke = '#1890ff';
      }
      if (active) {
        metadata.attrs.body.fill = '#f6ffed';
      }

      return metadata;
    },
  }, true);
}

/** 注册所有原生 SVG 节点 */
export function registerNativeNodes() {
  registerShape('opm-object', objectNodeMarkup, {
    // headerText: { ...headerText, refX: '50%', refY: 20 },
    label: { ...commonText, refX: '50%', refY: '55%' },
  });
  registerShape('opm-option', optionNodeMarkup, {
    label: { ...commonText, refX: '50%', refY: '55%' },
  }, optionPorts);
  registerShape('opm-process', processNodeMarkup, {
    body: { refCx: '50%', refCy: '50%', refRx: '50%', refRy: '50%' },
    headerText: { ...processHeaderText, refX: '50%', refY: '26%' },
    label: { ...commonText, refX: '50%', refY: '55%' },
  });
  registerShape('opm-state', stateNodeMarkup, {
    label: { ...commonText, refX: '50%', refY: '50%' },
  });
  registerShape('opm-value', valueNodeMarkup, {
    label: { ...commonText, refX: '50%', refY: '50%' },
  });
}
